import { useState } from 'react';
import {
  Box,
  Paper,
  Tab,
  Tabs,
  Typography,
  Chip,
} from '@mui/material';
import LoginRoundedIcon from '@mui/icons-material/LoginRounded';
import AppRegistrationRoundedIcon from '@mui/icons-material/AppRegistrationRounded';
import { AUTH_ACCENT_MID, AUTH_GRADIENT, authBrandTextSx } from '../authFormTheme';
import LoginScreen from './LoginScreen';
import RegisterScreen from './RegisterScreen';
import ForgotPasswordScreen from './ForgotPasswordScreen';

function AuthScreen({ onLoginSuccess }) {
  const [activeTab, setActiveTab] = useState(() =>
    (typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('ref'))
      || localStorage.getItem('eleven_games_referral')
      ? 'register'
      : 'login',
  );
  const tabValue = activeTab === 'login' ? 0 : activeTab === 'register' ? 1 : false;

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background:
          'radial-gradient(circle at 10% 0%, rgba(255,107,107,0.18), transparent 45%), #0a0f1a',
      }}
    >
      <Box
        sx={{
          background: 'linear-gradient(135deg, #0a0f1a 0%, #0f172a 45%, rgba(255,107,107,0.22) 100%)',
          color: '#fff',
          px: 3,
          pt: 3,
          pb: 3.25,
          borderBottomLeftRadius: 26,
          borderBottomRightRadius: 26,
          boxShadow: '0 12px 30px rgba(0,0,0,0.2)',
          position: 'relative',
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: 0,
            left: 20,
            right: 20,
            height: 2,
            background: AUTH_GRADIENT,
            borderRadius: 2,
          },
        }}
      >
        <Chip
          size="small"
          label="Secure Access"
          sx={{
            bgcolor: 'rgba(255,107,107,0.15)',
            color: AUTH_ACCENT_MID,
            fontWeight: 800,
            fontSize: '0.72rem',
            height: 24,
            border: '1px solid rgba(255,148,114,0.35)',
          }}
        />
        <Typography variant="h5" sx={{ mt: 1, ...authBrandTextSx }}>
          Eleven Games
        </Typography>
        <Typography
          sx={{
            mt: 0.6,
            maxWidth: 320,
            lineHeight: 1.45,
            fontWeight: 600,
            fontSize: '0.9rem',
            opacity: 0.95,
          }}
        >
          {activeTab === 'login'
            ? 'Welcome back. Log in to continue your game journey.'
            : activeTab === 'register'
              ? 'Create your Eleven Games account in under a minute.'
              : 'Enter your phone and set a new password'}
        </Typography>
      </Box>

      <Paper
        elevation={0}
        sx={{
          m: 2,
          mt: 2.5,
          flex: 1,
          bgcolor: 'rgba(10,11,18,0.92)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          borderRadius: 4,
          boxShadow: '0 16px 36px rgba(5,10,25,0.28)',
        }}
      >
        <Tabs
          value={tabValue}
          onChange={(_, newValue) => setActiveTab(newValue === 0 ? 'login' : 'register')}
          variant="fullWidth"
          textColor="primary"
          indicatorColor="primary"
          sx={{
            borderBottom: '1px solid rgba(255,255,255,0.08)',
            display: activeTab === 'forgot' ? 'none' : 'flex',
            px: 1,
            pt: 0.5,
            pb: 0.5,
            minHeight: 52,
            '& .MuiTab-root': {
              color: 'rgba(255,255,255,0.7)',
              fontWeight: 700,
              minHeight: 52,
              gap: 0.75,
            },
            '& .MuiTab-root.Mui-selected': {
              background: AUTH_GRADIENT,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              '& .MuiSvgIcon-root': {
                color: AUTH_ACCENT_MID,
                WebkitTextFillColor: 'initial',
              },
            },
            '& .MuiTabs-indicator': {
              background: AUTH_GRADIENT,
            },
          }}
        >
          <Tab icon={<LoginRoundedIcon />} iconPosition="start" label="Login" />
          <Tab
            icon={<AppRegistrationRoundedIcon />}
            iconPosition="start"
            label="Register"
          />
        </Tabs>

        <Box sx={{ px: 2, py: 2.5, overflowY: 'auto' }}>
          {activeTab === 'login' ? (
            <LoginScreen
              onGoRegister={() => setActiveTab('register')}
              onGoForgotPassword={() => setActiveTab('forgot')}
              onLoginSuccess={onLoginSuccess}
            />
          ) : activeTab === 'register' ? (
            <RegisterScreen
              onGoLogin={() => setActiveTab('login')}
              onRegisterSuccess={onLoginSuccess}
            />
          ) : (
            <ForgotPasswordScreen
              onBack={() => setActiveTab('login')}
              onResetSuccess={() => setActiveTab('login')}
            />
          )}
        </Box>
      </Paper>
    </Box>
  );
}

export default AuthScreen;
